import { useLocation } from 'react-router-dom';
import type { RequestOriginContext } from '@pcp/shared';
import { usePageChromeStore, matchRouteEntity, matchRouteTitle, entityKindLabel } from './pageChrome';

/**
 * Build the "where the user was" context for a new feature/bug request: the current
 * route + search, a human page title (the runtime chrome title — e.g. a ticket
 * subject — else the static section title), and, on a detail route, the entity it
 * shows ({kind,id}). Pathname-only entity resolution means this also works from the
 * topbar request tray (which lives outside the routed subtree). The caller freezes
 * the result at open time and lets the user include/exclude it.
 */
export function useOriginContext(): RequestOriginContext {
  const loc = useLocation();
  const chromeTitle = usePageChromeStore((s) => s.title);
  const entity = matchRouteEntity(loc.pathname);
  const pageTitle = chromeTitle || matchRouteTitle(loc.pathname) || undefined;
  return {
    route: loc.pathname,
    search: loc.search || undefined,
    pageTitle,
    entityKind: entity?.kind,
    entityId: entity?.id || undefined,
    capturedAt: new Date().toISOString(),
  };
}

/** A short human label for an origin context, e.g. "Ticket 123" or a page title. */
export function originContextLabel(c: RequestOriginContext | null | undefined): string {
  if (!c) return '';
  if (c.entityKind && c.entityId) return `${entityKindLabel(c.entityKind)} ${c.entityId}`;
  return c.pageTitle || c.route || '';
}
